import React, { useState, useEffect } from "react";
import { Add, Remove, Close } from "@material-ui/icons";
import { FiTruck, FiSettings, FiShoppingCart, FiPlus, FiMinus } from "react-icons/fi";
import StepCodeTag from "../../../components/shared/StepCodeTag";
import { PRODUCTION_STEP_CODES } from "../../../constants/workflowStepCodes";

const ProductionTab = ({
    productionUsageCart,
    handleProductionMaterialQuantity,
    handleAddCustomAddOnProduction,
    handlePosCreateProductionUsage,
    setFilterList,
    products,
    setColor,
    setProduct,
    setItemDetails,
    setColorPicker,
    orderData,
    handleSubmitProduction,
    handleConfirmProductionCompleted,
    superadmin,
    manager,
    production,
    productionManager,
    hasProductionManagerAccess,
    outright,
    hq,
}) => {
    const canEditProductionUsage = orderData?.status !== "completed" &&
                                    (superadmin || manager || production || hasProductionManagerAccess);

    const canEditQuantity = (orderData?.status === "production" || orderData?.status === "purchaseApproved") &&
                            (superadmin || manager || production || hasProductionManagerAccess);

    const [selectMaterialsModalOpen, setSelectMaterialsModalOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth <= 768);
    const [addOnModalOpen, setAddOnModalOpen] = useState(false);
    const [addOnForm, setAddOnForm] = useState({ name: "", quantity: "1", price: "0" });
    const [addOnSourceProduct, setAddOnSourceProduct] = useState(null);

    const isAddOnTitle = (title) => {
        const t = (title || "").trim().toLowerCase();
        return t === "add on" || t === "add-on" || t === "addon";
    };

    const openAddOnModal = (product) => {
        if (!canEditProductionUsage) return;
        setAddOnSourceProduct(product || null);
        setAddOnForm({ name: "", quantity: "1", price: "0" });
        setAddOnModalOpen(true);
        if (isMobile) setSelectMaterialsModalOpen(false);
    };

    const closeAddOnModal = () => {
        setAddOnModalOpen(false);
        setAddOnForm({ name: "", quantity: "1", price: "0" });
        setAddOnSourceProduct(null);
    };

    const handleSubmitAddOn = () => {
        const trimmedName = addOnForm.name.trim();
        if (!trimmedName) {
            alert("Please enter an item name for the Add On.");
            return;
        }
        const parsedQuantity = parseInt(addOnForm.quantity, 10);
        const quantity = Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;
        const parsedPrice = parseFloat(addOnForm.price);
        const price = Number.isFinite(parsedPrice) && parsedPrice >= 0 ? parsedPrice : 0;

        if (handleAddCustomAddOnProduction) {
            handleAddCustomAddOnProduction(trimmedName, quantity, price, addOnSourceProduct);
        }
        closeAddOnModal();
    };

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const selectMaterialsContent = (
        <>
            <div className="checkoutButtons">
                <div
                    className={`salesButton bronze ${!canEditProductionUsage ? "disabled" : ""}`}
                    onClick={() => {
                        if (canEditProductionUsage) {
                            setFilterList({ type: "watch" });
                        }
                    }}
                    style={{
                        opacity: canEditProductionUsage ? 1 : 0.5,
                        cursor: canEditProductionUsage ? "pointer" : "not-allowed",
                    }}
                >
                    Select Materials
                </div>
            </div>
            <div className="posContainer">
                {products ? (
                    products
                        .filter((product) => {
                            const normalizedTitle = (product.title || "").trim().toLowerCase();
                            const excludedTitles = [
                                "acp",
                                "glass",
                                "metal deck",
                                "polycarbonate",
                                "awning",
                                "others",
                                "other",
                            ];
                            return !excludedTitles.includes(normalizedTitle);
                        })
                        .map((product, i) => (
                            <div
                                key={i}
                                className="posItem"
                                onClick={() => {
                                    if (!canEditProductionUsage) return;
                                    if (isAddOnTitle(product.title)) {
                                        openAddOnModal(product);
                                        return;
                                    }
                                    setColor(product.color);
                                    setItemDetails({
                                        name: `${product.title}`,
                                        isProductionMaterial: true,
                                    });
                                    window.scrollTo(0, 0);
                                    setColorPicker(true);
                                    if (isMobile) setSelectMaterialsModalOpen(false);
                                }}
                                style={{
                                    opacity: canEditProductionUsage ? 1 : 0.5,
                                    cursor: canEditProductionUsage ? "pointer" : "not-allowed",
                                }}
                            >
                                <StepCodeTag code={PRODUCTION_STEP_CODES.materials} />
                                <div className="productDetails">{product.title}</div>
                            </div>
                        ))
                ) : (
                    <></>
                )}
            </div>
        </>
    );

    return (
        <div className="p-8 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
            {/* Enhanced Header Section */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">
                            Production Usage Management
                        </h1>
                        <p className="text-gray-600 text-sm">
                            Track materials used in production and manage production workflow
                        </p>
                    </div>
                    
                    {/* Enhanced Action Buttons */}
                    <div className="flex flex-wrap gap-3">
                        {(superadmin || manager || production || hasProductionManagerAccess) && orderData?.status === "purchaseApproved" && (
                            <button
                                className="group flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl shadow-lg hover:shadow-xl hover:from-purple-700 hover:to-purple-800 focus:outline-none focus:ring-4 focus:ring-purple-300 transform hover:scale-105 transition-all duration-200"
                                onClick={handleSubmitProduction}
                            >
                                <StepCodeTag code={PRODUCTION_STEP_CODES.submitProduction} />
                                <FiSettings className="h-5 w-5 group-hover:animate-spin" />
                                <span className="font-semibold">Submit Production</span>
                            </button>
                        )}
                        {(superadmin || manager || production || hasProductionManagerAccess) && orderData?.status === "production" && (
                            <button
                                style={{ background: "linear-gradient(to right, #16a34a, #15803d)", color: "#fff" }}
                                className="hidden md:flex group items-center gap-2 px-6 py-3 text-white rounded-xl shadow-lg hover:shadow-xl focus:outline-none transform hover:scale-105 transition-all duration-200"
                                onClick={handleConfirmProductionCompleted}
                            >
                                <StepCodeTag code={PRODUCTION_STEP_CODES.completeProduction} />
                                <FiSettings className="h-5 w-5 group-hover:animate-pulse" />
                                <span className="font-semibold">Confirm Production</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
            <div className="pos">
                <div className="userContainer">
                    <div className="checkoutButtons">
                        <div className="salesButton bronze">
                            Production Usage
                        </div>
                    </div>
                    <div className="w-full overflow-x-auto">
                        <table className="w-full min-w-max border-collapse">
                            <thead>
                                <tr className="bg-gradient-to-r from-gray-800 to-gray-900 text-white">
                                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-gray-700">Item</th>
                                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-gray-700">Item Code</th>
                                    <th className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider">Quantity</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {productionUsageCart && productionUsageCart.length > 0 ? (
                                    productionUsageCart.map((material, i) => (
                                        <tr 
                                            key={material.uniqueId || i}
                                            className={`transition-colors duration-150 ${material.record === "late" ? "bg-yellow-200 hover:bg-yellow-300" : material.record === "normal" ? "bg-green-200 hover:bg-green-300" : "hover:bg-gray-50"}`}
                                        >
                                            {/* Item */}
                                            <td className="px-3 py-3 text-sm font-medium text-gray-900 capitalize whitespace-nowrap border-r border-gray-200">
                                                {material.activeColor?.type || material.type}
                                            </td>
                                            
                                            {/* Item Code */}
                                            <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap border-r border-gray-200">
                                                {material.itemCode || material.color?.itemCode || material.activeColor?.itemCode || material.code || 'N/A'}
                                            </td>
                                            
                                            {/* Quantity */}
                                            <td className="px-3 py-3">
                                                <div className="flex items-center justify-center gap-2">
                                                    {(material.record === "normal" ? canEditQuantity : canEditProductionUsage) ? (
                                                        <>
                                                            <button
                                                                onClick={() =>
                                                                    handleProductionMaterialQuantity(
                                                                        material,
                                                                        -1,
                                                                        Math.max(0, material.amount - 1)
                                                                    )
                                                                }
                                                                className="w-8 h-8 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors shadow-sm"
                                                            >
                                                                <Remove style={{ fontSize: "1.2rem" }} />
                                                            </button>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                value={material.amount}
                                                                onChange={(e) => {
                                                                    const newAmount = parseInt(e.target.value) || 0;
                                                                    handleProductionMaterialQuantity(
                                                                        material,
                                                                        newAmount - material.amount,
                                                                        newAmount
                                                                    );
                                                                }}
                                                                className="w-16 px-2 py-1.5 text-sm text-center border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-medium"
                                                            />
                                                            <button
                                                                onClick={() =>
                                                                    handleProductionMaterialQuantity(
                                                                        material,
                                                                        1,
                                                                        material.amount + 1
                                                                    )
                                                                }
                                                                className="w-8 h-8 flex items-center justify-center bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors shadow-sm"
                                                            >
                                                                <Add style={{ fontSize: "1.2rem" }} />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <span className="text-sm font-medium text-gray-900">{material.amount}</span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={3} className="px-3 py-8 text-center text-sm text-gray-500">
                                            No materials added yet
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="summary w-full flex lightblue">
                        <div className="summarysubtotal">Total Materials</div>
                        <div className="summarysubtotal">
                            {productionUsageCart
                                ? productionUsageCart.reduce(
                                      (total, item) => total + item.amount,
                                      0
                                  )
                                : 0}
                        </div>
                    </div>

                    {canEditProductionUsage && (
                        <div className="checkoutButtons">
                            <div
                                className="salesButton sales"
                                onClick={handlePosCreateProductionUsage}
                            >
                                Update Production Usage
                            </div>
                        </div>
                    )}
                </div>

                {/* Desktop: show Select Materials section inline. Mobile: show icon that opens modal */}
                <div
                    className={`posWrapper lightred purchase-order-select-materials-section ${isMobile ? "purchase-order-select-materials-hidden" : ""}`}
                >
                    {selectMaterialsContent}
                </div>
                {isMobile && (
                    <>
                        <button
                            type="button"
                            className="purchase-order-select-materials-trigger"
                            onClick={() => setSelectMaterialsModalOpen(true)}
                            aria-label="Select materials"
                        >
                            <FiShoppingCart className="purchase-order-select-materials-trigger-icon" />
                            <span>Select Materials</span>
                        </button>
                        {selectMaterialsModalOpen && (
                            <>
                                <div
                                    className="purchase-order-select-materials-backdrop"
                                    onClick={() => setSelectMaterialsModalOpen(false)}
                                    aria-hidden="true"
                                />
                                <div className="purchase-order-select-materials-modal">
                                    <div className="purchase-order-select-materials-modal-header">
                                        <h3>Select Materials</h3>
                                        <button
                                            type="button"
                                            className="purchase-order-select-materials-modal-close"
                                            onClick={() => setSelectMaterialsModalOpen(false)}
                                            aria-label="Close"
                                        >
                                            <Close />
                                        </button>
                                    </div>
                                    <div className="purchase-order-select-materials-modal-body">
                                        {selectMaterialsContent}
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
            {addOnModalOpen && (
                <div className="product-picker-modal-root" role="dialog" aria-modal="true">
                    <div
                        className="product-picker-backdrop"
                        style={{
                            position: "fixed",
                            inset: 0,
                            background: "black",
                            opacity: 0.75,
                            zIndex: 10,
                        }}
                        onClick={closeAddOnModal}
                        aria-hidden="true"
                    />
                    <div
                        className="product-picker-overlay"
                        style={{
                            position: "fixed",
                            inset: 0,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            zIndex: 11,
                            pointerEvents: "none",
                        }}
                    >
                        <div
                            onClick={closeAddOnModal}
                            style={{
                                height: "100%",
                                width: "50%",
                                pointerEvents: "auto",
                            }}
                            className="add-on-empty-side"
                        />
                        <div
                            className="product-picker-panel"
                            style={{
                                height: "100%",
                                width: "50%",
                                display: "flex",
                                background: "white",
                                pointerEvents: "auto",
                            }}
                        >
                            <div
                                className="product-picker-panel-inner"
                                style={{
                                    background: "white",
                                    height: "100%",
                                    width: "100%",
                                    display: "grid",
                                }}
                            >
                                <div className="purchase-order-select-materials-modal-header product-picker-mobile-header">
                                    <h3>Add On</h3>
                                    <button
                                        type="button"
                                        className="purchase-order-select-materials-modal-close"
                                        onClick={closeAddOnModal}
                                        aria-label="Close"
                                    >
                                        <Close />
                                    </button>
                                </div>
                                <div
                                    className="product-picker-modal-body"
                                    style={{
                                        overflowY: "auto",
                                        overflowX: "hidden",
                                        height: "100vh",
                                        background: "rgb(240, 240, 255)",
                                    }}
                                >
                                    <div className="itemDetailsContainer">
                                        <button
                                            type="button"
                                            className="close-menu-button"
                                            onClick={closeAddOnModal}
                                            aria-label="Close"
                                        >
                                            <Close />
                                        </button>

                                        <div className="summary">
                                            <div className="addProductItem fullWidth">
                                                <label className="inline-flex items-center gap-2 flex-wrap">
                                                    <StepCodeTag code={PRODUCTION_STEP_CODES.itemCodeAndName} />
                                                    Item Name
                                                </label>
                                                <input
                                                    name="addOnName"
                                                    type="text"
                                                    autoFocus
                                                    placeholder="Enter item name"
                                                    value={addOnForm.name}
                                                    onChange={(e) =>
                                                        setAddOnForm((prev) => ({
                                                            ...prev,
                                                            name: e.target.value,
                                                        }))
                                                    }
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            handleSubmitAddOn();
                                                        }
                                                    }}
                                                />
                                            </div>

                                            <div className="addProductItem fullWidth">
                                                <label className="inline-flex items-center gap-2 flex-wrap">
                                                    <StepCodeTag code={PRODUCTION_STEP_CODES.quantity} />
                                                    Quantity
                                                </label>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "10px",
                                                    }}
                                                >
                                                    <Remove
                                                        style={{
                                                            fontSize: "1.2rem",
                                                            cursor: "pointer",
                                                            padding: "4px",
                                                            borderRadius: "4px",
                                                            border: "1px solid #d1d5db",
                                                            backgroundColor: "#f9fafb",
                                                        }}
                                                        onClick={() => {
                                                            const current =
                                                                parseInt(addOnForm.quantity, 10) || 1;
                                                            if (current > 1) {
                                                                setAddOnForm((prev) => ({
                                                                    ...prev,
                                                                    quantity: String(current - 1),
                                                                }));
                                                            }
                                                        }}
                                                    />
                                                    <input
                                                        name="quantity"
                                                        type="number"
                                                        min="1"
                                                        placeholder="Quantity"
                                                        value={addOnForm.quantity}
                                                        onChange={(e) => {
                                                            const value =
                                                                parseInt(e.target.value, 10) || 1;
                                                            setAddOnForm((prev) => ({
                                                                ...prev,
                                                                quantity: String(Math.max(1, value)),
                                                            }));
                                                        }}
                                                        style={{
                                                            flex: 1,
                                                            textAlign: "center",
                                                            padding: "8px",
                                                        }}
                                                    />
                                                    <Add
                                                        style={{
                                                            fontSize: "1.2rem",
                                                            cursor: "pointer",
                                                            padding: "4px",
                                                            borderRadius: "4px",
                                                            border: "1px solid #d1d5db",
                                                            backgroundColor: "#f9fafb",
                                                        }}
                                                        onClick={() => {
                                                            const current =
                                                                parseInt(addOnForm.quantity, 10) || 1;
                                                            setAddOnForm((prev) => ({
                                                                ...prev,
                                                                quantity: String(current + 1),
                                                            }));
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            <div className="addProductItem fullWidth">
                                                <label>Price</label>
                                                <input
                                                    name="price"
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    placeholder="Enter price"
                                                    value={addOnForm.price}
                                                    onChange={(e) =>
                                                        setAddOnForm((prev) => ({
                                                            ...prev,
                                                            price: e.target.value,
                                                        }))
                                                    }
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            handleSubmitAddOn();
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            className="productListEdit mt-2"
                                            onClick={handleSubmitAddOn}
                                        >
                                            <span className="inline-flex items-center gap-2">
                                                <StepCodeTag code={PRODUCTION_STEP_CODES.addToMaterials} />
                                                Add To Materials
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductionTab;

